/**
 * ResultsTable Component
 * TanStack Table v8 implementation with HTML table elements
 * NO MUI Table components - uses standard HTML <table>, <thead>, <tbody>, <tr>, <td>
 *
 * @module MetricDataDevTool/components/ResultsTable
 */

import React from 'react';
import { flexRender } from '@tanstack/react-table';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  IconButton,
  Stack
} from '@mui/material';
import {
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon
} from '@mui/icons-material';
import { useTableConfig } from '../hooks/useTableConfig';
import { TABLE_CONFIG } from '../constants';

/**
 * ResultsTable component
 * @param {Object} props - Component props
 * @param {Array} props.data - Table data
 * @param {Object} props.aggregationConfig - Aggregation configuration
 * @param {boolean} [props.loading=false] - Loading state
 * @param {string} [props.emptyMessage='No data to display'] - Empty state message
 */
export default function ResultsTable({
  data = [],
  aggregationConfig,
  loading = false,
  emptyMessage = 'No data to display'
}) {
  const { table } = useTableConfig(data, aggregationConfig);

  // Empty state
  if (!loading && data.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  // Loading state
  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex + 1;

  return (
    <Box>
      {/* Table */}
      <Box sx={{ overflowX: 'auto', mb: 2 }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.875rem'
          }}
        >
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr
                key={headerGroup.id}
                style={{
                  borderBottom: '2px solid #e0e0e0',
                  position: 'sticky',
                  top: 0,
                  backgroundColor: '#fff',
                  zIndex: 1
                }}
              >
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    style={{
                      padding: '12px 16px',
                      textAlign: header.column.columnDef.meta?.align || 'left',
                      fontWeight: 600,
                      color: '#424242',
                      cursor: header.column.getCanSort() ? 'pointer' : 'default'
                    }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {{
                        asc: ' 🔼',
                        desc: ' 🔽',
                      }[header.column.getIsSorted()] ?? null}
                    </Box>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => (
              <tr
                key={row.id}
                style={{
                  borderBottom: '1px solid #e0e0e0'
                }}
                sx={{
                  '&:hover': {
                    backgroundColor: '#f5f5f5'
                  }
                }}
              >
                {row.getVisibleCells().map(cell => (
                  <td
                    key={cell.id}
                    style={{
                      padding: '12px 16px',
                      textAlign: cell.column.columnDef.meta?.align || 'left'
                    }}
                  >
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext()
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>

      {/* Pagination Controls */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 2 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Rows per page:
          </Typography>
          <Select
            value={table.getState().pagination.pageSize}
            onChange={e => table.setPageSize(Number(e.target.value))}
            size="small"
            sx={{ minWidth: 80 }}
          >
            {TABLE_CONFIG.ROWS_PER_PAGE_OPTIONS.map(size => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Typography variant="body2" color="text.secondary">
          Page {currentPage} of {pageCount} ({data.length} total rows)
        </Typography>

        <Stack direction="row" spacing={0.5}>
          <IconButton
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
            size="small"
          >
            <FirstPageIcon />
          </IconButton>
          <IconButton
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            size="small"
          >
            <PrevIcon />
          </IconButton>
          <IconButton
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            size="small"
          >
            <NextIcon />
          </IconButton>
          <IconButton
            onClick={() => table.setPageIndex(pageCount - 1)}
            disabled={!table.getCanNextPage()}
            size="small"
          >
            <LastPageIcon />
          </IconButton>
        </Stack>
      </Stack>
    </Box>
  );
}
